import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import Modal from "../../components/shared/Modal/Modal";
import { useTranslation } from "react-i18next";
import { NextExportPreview, ServiceNextProject } from "../../services/ServiceNextProject";
import NextConflictSection from "./NextConflictSection";
import { WarningOutlineIcon } from "../../components/svgs";
import styles from './NextConflictSection.module.scss';
import { ConflictSelection, buildFullSelection, countSelectedFields, selectionToUpdates } from "./nextConflictSelection";

type Props = {
  show: boolean,
  setShow: Dispatch<SetStateAction<boolean>>,
  nextProjectId: number | null,
  onSuccess: (response: any) => void,
  onError: (error: any) => void,
}

const UpdateFromNextModal: FC<Props> = ({ show, setShow, nextProjectId, onSuccess, onError }) => {
  const [t] = useTranslation('projects');
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<NextExportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [hasOpenedReview, setHasOpenedReview] = useState(false);
  const [selection, setSelection] = useState<ConflictSelection>({});

  const fetchPreview = async () => {
    if (!nextProjectId) return;
    setPreviewLoading(true);
    setPreviewFailed(false);
    try {
      const response = await ServiceNextProject.previewImport(nextProjectId);
      setPreview(response);
      setSelection(buildFullSelection(response.conflicts));
    } catch (error) {
      console.error('Failed to preview Next import:', error);
      setPreview(null);
      setPreviewFailed(true);
    }
    setPreviewLoading(false);
  };

  useEffect(() => {
    if (!show) {
      setPreview(null);
      setPreviewFailed(false);
      setReviewExpanded(false);
      setHasOpenedReview(false);
      setSelection({});
      return;
    }
    fetchPreview();
  }, [show, nextProjectId]);

  const conflicts = preview?.conflicts ?? [];
  const selectedFieldsCount = countSelectedFields(selection, conflicts);
  const mustReviewFirst = conflicts.length > 0 && !hasOpenedReview;

  const handleImport = async () => {
    if (!nextProjectId) return;
    setImporting(true);
    try {
      const response = await ServiceNextProject.importProjects(
        [nextProjectId],
        conflicts.length > 0 ? selectionToUpdates(selection, conflicts) : undefined,
      );
      onSuccess(response);
      setShow(false);
    } catch (error) {
      onError(error);
    }
    setImporting(false);
  };

  return (
    <Modal
      title={<span className={styles.modalTitle}>{t('updateFromNextModal.headline', 'Hämta från Next Project')}</span>}
      show={show}
      setShow={setShow}
      buttonInfo={{
        label: selectedFieldsCount > 0
          ? t('updateFromNextModal.button.importWithUpdates', 'Hämta & uppdatera {{count}} fält', { count: selectedFieldsCount })
          : t('updateFromNextModal.button.import', 'Hämta'),
        action: handleImport,
        disabled: importing || previewLoading || previewFailed || mustReviewFirst,
        loaderShow: importing,
      }}
    >
      <div className={styles.modalBody}>
        {previewFailed ? (
          <div className={styles.pushWarning}>
            <WarningOutlineIcon size={15} className={styles.warningIcon} />
            <div>
              {t('updateFromNextModal.conflicts.previewFailed', 'Det gick inte att jämföra värden med Next. Försök igen.')}
              {' '}
              <span className={styles.retryLink} onClick={fetchPreview}>
                {t('updateFromNextModal.conflicts.retry', 'Försök igen')}
              </span>
            </div>
          </div>
        ) : !previewLoading && conflicts.length === 0 && (preview?.willBeRecreated.length ?? 0) === 0 && (preview?.missingAccounts?.length ?? 0) === 0 ? (
          <div className={styles.noConflictsText}>
            {t('updateFromNextModal.noConflicts', 'Inga skillnader hittades. Nya inköp och projektuppgifter hämtas från Next.')}
          </div>
        ) : null}
        {!previewFailed && (
          <NextConflictSection
            preview={preview}
            loading={previewLoading}
            direction="import"
            expanded={reviewExpanded}
            onToggleExpanded={() => {
              setReviewExpanded(!reviewExpanded);
              setHasOpenedReview(true);
            }}
            selection={selection}
            onSelectionChange={setSelection}
          />
        )}
      </div>
    </Modal>
  );
};

export default UpdateFromNextModal;
